import React, { Fragment, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import Box from '@quintoandar/cozy-core/Box';
import Typography from '@quintoandar/cozy-core/Typography';
import { ChipAssistGroup, ChipAssistItem } from '@quintoandar/cozy-core/ChipAssist';
import tokens from '@quintoandar/cozy-theme/tokens/js';

import AutoComplete from 'components/Autocomplete';
import { cleanUpCondoName } from 'utils/cleanUpCondoName';
import ErrorBoundary from 'components/ErrorBoundary';
import { logUncaught } from 'utils/log';

import messages from './messages';
import { updateCondoNameAction } from '../../actions';

const FORM_INDEX = {
  YES: 0,
  NO: 1,
  I_DON_T_KNOW: 2,
};

const options = [
  <FormattedMessage {...messages.yes} />,
  <FormattedMessage {...messages.no} />,
  <FormattedMessage {...messages.iDoNotKnowTheName} />,
];

const CondoName = ({ photoSessionDetail, handleCondoName, house, shouldValidate, checkCondoNameIsValid }) => {
  const [choice, setChoice] = useState(null);
  const [isValid, setIsValid] = useState(true);

  const reliableCondoName = photoSessionDetail.condoName;
  const suggestedCondoName = house.items.condoName.value;
  const isReliableCondoNameAvailable = !reliableCondoName;

  const handleChoice = (index) => {
    setChoice(index);
    setIsValid(true);

    if (index === FORM_INDEX.YES) {
      handleCondoName(reliableCondoName);
    }

    if (index === FORM_INDEX.NO) {
      handleCondoName('');
    }

    if (index === FORM_INDEX.I_DON_T_KNOW) {
      handleCondoName(null);
    }
  };

  const handleCheckBox = (checkbox) => checkbox ? handleChoice(FORM_INDEX.I_DON_T_KNOW) : handleChoice(FORM_INDEX.NO);

  const validateInitialChoice = (initialChoice) => {
    if (reliableCondoName === suggestedCondoName) {
      return initialChoice === FORM_INDEX.YES;
    } else if (suggestedCondoName === null) {
      return initialChoice === FORM_INDEX.I_DON_T_KNOW;
    } else if (suggestedCondoName !== '') {
      return initialChoice === FORM_INDEX.NO;
    }

    return false;
  };

  useEffect(() => {
    if (reliableCondoName === suggestedCondoName) {
      setChoice(FORM_INDEX.YES);
    } else if (suggestedCondoName === null) {
      setChoice(FORM_INDEX.I_DON_T_KNOW);
    } else if (suggestedCondoName !== '') {
      setChoice(FORM_INDEX.NO);
    }
  }, []);

  useEffect(() => {
    if (shouldValidate) {
      setIsValid(checkCondoNameIsValid());
    }
  }, [shouldValidate]);


  return (<Fragment>
    {
      reliableCondoName ? <Box>
        <Typography mb={tokens.alias.spacing.hxxsmall} level="bodyStrong">
          <FormattedMessage
            {...messages.condoName}
            values={{ condoName: cleanUpCondoName(reliableCondoName) }}
          />
        </Typography>
        <Typography mb={tokens.alias.spacing.small} level="body" kind="lighter">
          <FormattedMessage
            {...messages.condoAddress} values={{
              address: photoSessionDetail.house.address,
              number: photoSessionDetail.house.number,
              neighborhood: photoSessionDetail.house.neighborhood,
              br: <br />,
              state: photoSessionDetail.house.state,
            }}
          />

        </Typography>
      </Box> : null
    }
    {reliableCondoName ? <><Typography mb={tokens.alias.spacing.xsmall}>
      <FormattedMessage {...messages.title} />
    </Typography>
    <ChipAssistGroup>
      {options.map((option, index) => {
        const handleChoiceOption = () => {
          handleChoice(index);
        };

        return (<ChipAssistItem
          id={`item-${index}`}
          label={option}
          key={index}
          onClick={handleChoiceOption}
          isInitialActive={validateInitialChoice(index)}
        />);
      })}
    </ChipAssistGroup></> : null}
    {choice === FORM_INDEX.NO || isReliableCondoNameAvailable
      ? <ErrorBoundary errorComponent={<></>} logError={logUncaught}>
        <AutoComplete
          inputValue={suggestedCondoName}
          handleCondoName={handleCondoName}
          showCheckbox={isReliableCondoNameAvailable}
          checkboxChecked={choice === FORM_INDEX.I_DON_T_KNOW}
          onChangeCheckbox={handleCheckBox}
        />
      </ErrorBoundary> : null}
    {!isValid ? <Typography kind="critical">
      <FormattedMessage {...messages.invalid} />
    </Typography> : null
    }
  </Fragment>);
};

CondoName.propTypes = {
  photoSessionDetail: PropTypes.object.isRequired,
  handleCondoName: PropTypes.func.isRequired,
  house: PropTypes.object.isRequired,
  shouldValidate: PropTypes.bool.isRequired,
  checkCondoNameIsValid: PropTypes.func.isRequired,
};

export const mapStateToProps = createStructuredSelector({});

export const mapDispatchToProps = (dispatch) => ({
  updateCondoName: ({ condoName }) =>
    dispatch(updateCondoNameAction({ condoName })),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(CondoName);
